import { useEffect, useRef } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { MapPin, Loader2, ExternalLink, AlertCircle } from 'lucide-react';
import { ProducerFormData, REGIONS } from '../types';
import { useGeocoding } from '../hooks/useGeocoding';
import { AddressAutocomplete } from '../components/AddressAutocomplete';
import { GeocodeCandidate } from '../hooks/useAddressSearch';
import { matchRegion } from '../utils/matchRegion';
import { toast } from '@/hooks/use-toast';
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { MAPLIBRE_STYLE_URL } from '@/lib/map/config';

interface LocationSectionProps {
  formData: ProducerFormData;
  updateField: <K extends keyof ProducerFormData>(key: K, value: ProducerFormData[K]) => void;
}

// Inline map preview component with card marker - same MapLibre setup as detail page
function InlineMapPreview({ 
  lat, 
  lng, 
  showHint, 
  producerName, 
  producerCity 
}: { 
  lat: number; 
  lng: number; 
  showHint?: boolean;
  producerName?: string;
  producerCity?: string;
}) {
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<maplibregl.Map | null>(null);
  const marker = useRef<maplibregl.Marker | null>(null);

  useEffect(() => {
    if (!mapContainer.current) return;

    // Cleanup previous map if exists
    if (map.current) {
      map.current.remove();
      map.current = null;
    }

    map.current = new maplibregl.Map({
      container: mapContainer.current,
      style: MAPLIBRE_STYLE_URL,
      center: [lng, lat], // Note: MapLibre uses [lng, lat] order
      zoom: 13,
      attributionControl: false,
    });

    // Create custom card marker
    const name = producerName || 'Din gård';
    const city = producerCity || '';
    
    const markerEl = document.createElement('div');
    markerEl.innerHTML = `
      <div style="
        display: flex;
        flex-direction: column;
        align-items: center;
        transform: translateY(-12px);
      ">
        <div style="
          background: white;
          border-radius: 12px;
          padding: 8px 12px;
          box-shadow: 0 8px 24px rgba(0,0,0,0.16);
          border: 1px solid rgba(0,0,0,0.06);
          text-align: center;
          min-width: 120px;
          max-width: 180px;
        ">
          <div style="font-weight: 600; font-size: 13px; line-height: 1.2; color: #1a1a1a; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">
            ${name}
          </div>
          ${city ? `<div style="font-size: 12px; color: rgba(0,0,0,0.6); margin-top: 2px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">${city}</div>` : ''}
        </div>
        <div style="
          width: 14px;
          height: 14px;
          background: hsl(18, 55%, 46%);
          border-radius: 999px;
          border: 2px solid white;
          box-shadow: 0 6px 14px rgba(0,0,0,0.18);
          margin-top: 6px;
        "></div>
      </div>
    `;

    marker.current = new maplibregl.Marker({ element: markerEl })
      .setLngLat([lng, lat])
      .addTo(map.current);

    return () => {
      map.current?.remove();
      map.current = null;
    };
  }, [lat, lng, producerName, producerCity]);

  const googleMapsUrl = `https://www.google.com/maps?q=${lat},${lng}`;

  return (
    <div className="md:col-span-2 space-y-3 mt-2">
      <div className="flex items-center gap-2 text-sm font-medium text-foreground">
        <MapPin className="h-4 w-4" />
        Kartförhandsvisning
      </div>

      {showHint && (
        <div className="flex items-start gap-2 text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-md p-2">
          <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
          <span>Kontrollera att pinnen hamnat rätt.</span>
        </div>
      )}

      <div
        ref={mapContainer}
        className="w-full h-[260px] rounded-lg border border-border overflow-hidden"
      />

      <div className="flex items-center justify-between gap-4">
        <p className="text-xs text-muted-foreground font-mono">
          {lat.toFixed(4)}, {lng.toFixed(4)}
        </p>
        <Button variant="outline" size="sm" asChild>
          <a href={googleMapsUrl} target="_blank" rel="noopener noreferrer">
            Öppna i Google Maps
            <ExternalLink className="ml-2 h-3 w-3" />
          </a>
        </Button>
      </div>
    </div>
  );
}

export function LocationSection({ formData, updateField }: LocationSectionProps) {
  const { geocode, isGeocoding, geocodeResult, geocodeStatus } = useGeocoding();
  const justGeocoded = geocodeStatus === 'success';

  const handleGeocode = async () => {
    const result = await geocode({
      address: formData.address,
      city: formData.city,
      region: formData.region,
    });

    if (result) {
      updateField('lat', result.lat.toString());
      updateField('lng', result.lng.toString());
    }
  };

  // Handle autocomplete selection
  const handleAddressSelect = (candidate: GeocodeCandidate) => {
    // Update address field with street (or keep user's input if no street)
    if (candidate.street) {
      updateField('address', candidate.street);
    }

    // Update city if available
    if (candidate.city) {
      updateField('city', candidate.city);
    }

    // Try to match region from county
    const matchedRegion = matchRegion(candidate.county);
    if (matchedRegion) {
      updateField('region', matchedRegion);
    }

    // Update coordinates
    updateField('lat', candidate.lat.toString());
    updateField('lng', candidate.lng.toString());

    // Show success toast
    toast({
      title: 'Adress hittad',
      description: `Koordinater uppdaterade: ${candidate.lat.toFixed(5)}, ${candidate.lng.toFixed(5)}`,
    });
  };

  // Parse and normalize coordinates (support Swedish decimal comma)
  const latNum = formData.lat ? Number(String(formData.lat).replace(',', '.')) : NaN;
  const lngNum = formData.lng ? Number(String(formData.lng).replace(',', '.')) : NaN;
  const hasValidCoordinates = !isNaN(latNum) && !isNaN(lngNum);

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-foreground">Adress och position</h3>
        <p className="text-sm text-muted-foreground mt-1">
          Var finns gården?
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl">
        <div className="md:col-span-2">
          <AddressAutocomplete
            value={formData.address}
            city={formData.city}
            region={formData.region}
            onChange={(value) => updateField('address', value)}
            onSelect={handleAddressSelect}
            placeholder="T.ex. Gårdsvägen 12, Gnesta"
          />
          <p className="text-xs text-muted-foreground mt-1.5">
            Börja skriva för att söka – välj ett förslag för att fylla i koordinater automatiskt.
          </p>
        </div>

        <div>
          <Label htmlFor="city">Ort</Label>
          <Input
            id="city"
            value={formData.city}
            onChange={(e) => updateField('city', e.target.value)}
            placeholder="T.ex. Gnesta"
          />
        </div>

        <div>
          <Label htmlFor="region">Landskap/Län</Label>
          <Select
            value={formData.region}
            onValueChange={(value) => updateField('region', value)}
          >
            <SelectTrigger>
              <SelectValue placeholder="Välj region" />
            </SelectTrigger>
            <SelectContent className="bg-popover">
              {REGIONS.map((region) => (
                <SelectItem key={region} value={region}>
                  {region}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label htmlFor="lat">Latitud</Label>
          <Input
            id="lat"
            type="number"
            step="any"
            value={formData.lat}
            onChange={(e) => updateField('lat', e.target.value)}
            placeholder="59.0512"
          />
        </div>

        <div>
          <Label htmlFor="lng">Longitud</Label>
          <Input
            id="lng"
            type="number"
            step="any"
            value={formData.lng}
            onChange={(e) => updateField('lng', e.target.value)}
            placeholder="17.3078"
          />
        </div>

        <div className="md:col-span-2 space-y-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleGeocode}
            disabled={isGeocoding || !formData.address || formData.address.trim().length < 4}
          >
            {isGeocoding ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Hämtar…
              </>
            ) : (
              <>
                <MapPin className="h-4 w-4 mr-2" />
                {formData.lat && formData.lng ? 'Uppdatera från adress' : 'Hämta från adress'}
              </>
            )}
          </Button>
          {geocodeResult && (
            <p className="text-xs text-muted-foreground">
              Hittade: {geocodeResult}
            </p>
          )}
        </div>

        {hasValidCoordinates && (
          <InlineMapPreview
            lat={latNum}
            lng={lngNum}
            showHint={justGeocoded}
            producerName={formData.name}
            producerCity={formData.city}
          />
        )}
      </div>
    </div>
  );
}
